import { Component } from '../../core/component.js';
import { api } from '../../api.js';
import { router } from '../../router.js';
import { eventBus } from '../../events.js';
import { escapeHtml, debounce } from '../../core/utils.js';
import { hasAnyPermission, hasPlanAnyFeature } from '../../modules/auth/auth-service.js';

const NAV_ITEMS = [
    { label: 'Главная',       path: '/',               group: 'Навигация' },
    { label: 'Календарь',     path: '/calendar',       group: 'Навигация', perms: ['calendar.view', 'calendar.view_own'] },
    { label: 'Клиенты',       path: '/clients',        group: 'Навигация', perms: ['clients.view', 'clients.view_own'] },
    { label: 'Сотрудники',    path: '/staff',          group: 'Навигация', perms: ['staff.view', 'staff.manage'] },
    { label: 'Услуги',        path: '/services',       group: 'Навигация', perms: ['services.view'] },
    { label: 'Финансы',       path: '/finance',        group: 'Навигация', perms: ['finance.view'], plan: ['finance', 'finance_basic'] },
    { label: 'Зарплаты',      path: '/salary',         group: 'Навигация', perms: ['salary.view', 'salary.view_own'], plan: ['salary'] },
    { label: 'Склад',         path: '/warehouse',      group: 'Навигация', perms: ['warehouse.view'], plan: ['warehouse'] },
    { label: 'Аналитика',     path: '/analytics',      group: 'Навигация', perms: ['analytics.view'], plan: ['analytics', 'analytics_basic'] },
    { label: 'Настройки',     path: '/settings',       group: 'Навигация' },
];

function isNavItemVisible(item) {
    if (item.perms && !hasAnyPermission(...item.perms)) return false;
    if (item.plan && !hasPlanAnyFeature(...item.plan)) return false;
    return true;
}

export class CommandPalette extends Component {
    constructor() {
        super();
        this.state = {
            open: false,
            query: '',
            results: [],
            highlighted: 0,
        };
        this._onKeydown = this._onKeydown.bind(this);
        this._search = debounce(this._doSearch.bind(this), 200);
    }

    template() {
        if (!this.state.open) return '';

        const { results, highlighted, query } = this.state;

        let groupedHtml = '';
        let currentGroup = '';
        let idx = 0;

        for (const item of results) {
            if (item.group !== currentGroup) {
                currentGroup = item.group;
                groupedHtml += `<div class="cp-group-title">${escapeHtml(currentGroup)}</div>`;
            }
            const cls = idx === highlighted ? 'cp-item cp-item--highlighted' : 'cp-item';
            groupedHtml += `<div class="${cls}" data-index="${idx}" data-path="${item.path || ''}" data-type="${item.type || 'nav'}">
                <span class="cp-item-label">${escapeHtml(item.label)}</span>
                ${item.hint ? `<span class="cp-item-hint">${escapeHtml(item.hint)}</span>` : ''}
            </div>`;
            idx++;
        }

        if (query && results.length === 0) {
            groupedHtml = '<div class="cp-empty">Ничего не найдено</div>';
        }

        return `
            <div class="cp-backdrop"></div>
            <div class="cp-dialog">
                <div class="cp-search">
                    <svg class="cp-search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="8" cy="8" r="5.5"/><line x1="12" y1="12" x2="16" y2="16"/>
                    </svg>
                    <input class="cp-input" type="text" placeholder="Поиск клиентов, услуг, навигация..." value="${escapeHtml(query)}" />
                    <kbd class="cp-esc">Esc</kbd>
                </div>
                <div class="cp-results">${groupedHtml}</div>
            </div>
        `;
    }

    afterRender() {
        if (!this.state.open) return;

        const input = this.el.querySelector('.cp-input');
        if (input) {
            input.addEventListener('input', (e) => {
                this.state.query = e.target.value;
                this.state.highlighted = 0;
                this._search(e.target.value);
            });
            setTimeout(() => input.focus(), 0);
        }

        const backdrop = this.el.querySelector('.cp-backdrop');
        if (backdrop) backdrop.addEventListener('click', () => this.close());

        this.el.querySelectorAll('.cp-item').forEach(item => {
            item.addEventListener('click', () => {
                this._selectItem(parseInt(item.dataset.index));
            });
        });
    }

    open() {
        this.state.open = true;
        this.state.query = '';
        this.state.results = NAV_ITEMS.filter(isNavItemVisible);
        this.state.highlighted = 0;
        this.render();
        document.addEventListener('keydown', this._onKeydown);
    }

    close() {
        this.state.open = false;
        this.render();
        document.removeEventListener('keydown', this._onKeydown);
    }

    toggle() {
        if (this.state.open) this.close();
        else this.open();
    }

    _onKeydown(e) {
        if (e.key === 'Escape') {
            this.close();
            return;
        }

        const len = this.state.results.length;
        if (!len) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.state.highlighted = (this.state.highlighted + 1) % len;
            this.render();
            this._scrollToHighlighted();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.state.highlighted = (this.state.highlighted - 1 + len) % len;
            this.render();
            this._scrollToHighlighted();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this._selectItem(this.state.highlighted);
        }
    }

    _scrollToHighlighted() {
        const el = this.el.querySelector('.cp-item--highlighted');
        if (el) el.scrollIntoView({ block: 'nearest' });
    }

    _selectItem(index) {
        const item = this.state.results[index];
        if (!item) return;

        this.close();

        if (item.path) {
            router.navigate(item.path);
        }
    }

    async _doSearch(query) {
        const q = query.trim().toLowerCase();

        if (!q) {
            this.state.results = NAV_ITEMS.filter(isNavItemVisible);
            this.render();
            return;
        }

        // Filter nav items by query and permissions
        const navResults = NAV_ITEMS
            .filter(item => isNavItemVisible(item) && item.label.toLowerCase().includes(q))
            .map(item => ({ ...item, type: 'nav' }));

        // Search API for clients/staff
        let apiResults = [];
        if (q.length >= 2) {
            try {
                const [clients, staff] = await Promise.allSettled([
                    api.getPaginated('/clients', { search: q, per_page: 5 }),
                    api.getPaginated('/staff', { search: q, per_page: 5 }),
                ]);

                if (clients.status === 'fulfilled' && clients.value?.data) {
                    apiResults.push(...clients.value.data.map(c => ({
                        label: c.name || `${c.first_name} ${c.last_name}`,
                        hint: c.phone || '',
                        path: `/clients/${c.id}`,
                        group: 'Клиенты',
                        type: 'client',
                    })));
                }

                if (staff.status === 'fulfilled' && staff.value?.data) {
                    apiResults.push(...staff.value.data.map(s => ({
                        label: s.name || `${s.first_name} ${s.last_name}`,
                        hint: s.position || '',
                        path: `/staff/${s.id}`,
                        group: 'Сотрудники',
                        type: 'staff',
                    })));
                }
            } catch {
                // API errors are non-critical for search
            }
        }

        this.state.results = [...apiResults, ...navResults];
        this.state.highlighted = 0;
        this.render();

        // Restore focus to input after re-render
        const input = this.el.querySelector('.cp-input');
        if (input) {
            input.focus();
            input.selectionStart = input.selectionEnd = input.value.length;
        }
    }

    mount(container) {
        super.mount(container);

        // Global Ctrl+K / Cmd+K shortcut
        this._globalKeydown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                this.toggle();
            }
        };
        document.addEventListener('keydown', this._globalKeydown);

        this._unsubOpen = eventBus.on('command-palette:open', () => this.open());

        return this;
    }

    destroy() {
        if (this._globalKeydown) {
            document.removeEventListener('keydown', this._globalKeydown);
        }
        if (this._unsubOpen) {
            this._unsubOpen();
            this._unsubOpen = null;
        }
        document.removeEventListener('keydown', this._onKeydown);
        super.destroy();
    }
}
